import { StudyGate, type StudyMissionSelectDto, type StudySelectDto } from '@@types/api.types';
import { BlueLabel } from '@components/shared/blue-label.component';
import { EmptyStateContainer } from '@components/shared/empty-state-container.component';
import { type SimpleTab, SimpleTabs } from '@components/shared/simple-tabs.component';
import { Skeleton } from '@components/ui/skeleton';
import {
  useGetMeiaRunCaseDashboard,
  useGetMeiaRunCases,
  useGetMeiaScoringOutputs,
  useGetMeiaStudyMissionThreads,
  useGetMeiaStudyP4E2eRows,
  useGetMeiaStudySwimlaneTasks,
  useGetMeiaStudyThreadSwimlanes,
  useGetMeiaStudyThreatTargets,
} from '@queries/meia.query';
import { LayoutDashboardIcon, LayoutGridIcon, Table2Icon } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { meiaRoute } from '../meia.constants';
import { MeiaP4AdvanceBar } from './meia-p4-advance-bar.component';
import { MeiaP4E2eView } from './meia-p4-e2e-view.component';
import { countUniqueTasks, countUniqueTechnologies } from './meia-p4-e2e.util';
import { MeiaP4KpiStrip } from './meia-p4-kpi-strip.component';
import { MeiaP4RunCasesView } from './meia-p4-run-cases-view.component';
import { formatEpochLabel } from './meia-p4-run-cases.util';
import { SCORING_CONFIG_AGGREGATION_OPTIONS } from './meia-p4-scoring-config-modal.component';
import { getBriefStudyVerdict, getDefaultBriefRunCaseId } from './meia-p5-brief.util';

type MeiaP4SubTab = 'e2e' | 'matrix' | 'runcases';

const isMeiaP4SubTab = (value: string | null): value is MeiaP4SubTab =>
  value === 'e2e' || value === 'matrix' || value === 'runcases';

const subTabTitles: Record<MeiaP4SubTab, string> = {
  e2e: 'End-to-end view — tasks × technologies',
  matrix: 'Measurement Matrix — measure status & advisory gaps',
  runcases: 'Run Cases — thread verdict & stage heatmap',
};

const subTabs: SimpleTab[] = [
  { icon: <LayoutDashboardIcon size={14} />, label: 'E2E View', value: 'e2e' },
  { icon: <LayoutGridIcon size={14} />, label: 'Run Cases', value: 'runcases' },
  { icon: <Table2Icon size={14} />, label: 'Measurement Matrix', value: 'matrix' },
];

type MeiaP4AuthoringProps = {
  canEdit: boolean;
  isMissionsLoading?: boolean;
  missions: StudyMissionSelectDto[];
  study: StudySelectDto;
  studyId: string;
};

export const MeiaP4Authoring: FC<MeiaP4AuthoringProps> = (props) => {
  const { canEdit, isMissionsLoading, missions, study, studyId } = props;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedThreadId, setSelectedThreadId] = useState<'all' | string>('all');

  const subtabParam = searchParams.get('subtab');
  const activeSubtab: MeiaP4SubTab = isMeiaP4SubTab(subtabParam) ? subtabParam : 'e2e';

  const { isLoading: isThreadsLoading, threads } = useGetMeiaStudyMissionThreads({ missions, studyId });
  const { isLoading: isSwimlanesLoading, swimlanes } = useGetMeiaStudyThreadSwimlanes({ studyId, threads });
  const { isLoading: isTasksLoading, tasks } = useGetMeiaStudySwimlaneTasks({ studyId, swimlanes });

  const hasMissions = missions.length > 0;
  const hasTasks = tasks.length > 0;
  const hasThreads = threads.length > 0;
  const isPrerequisiteLoading = isMissionsLoading || isThreadsLoading || isSwimlanesLoading || isTasksLoading;
  const canLoadP4Data = !isPrerequisiteLoading && hasMissions && hasThreads && hasTasks;

  const { data: rows = [], isLoading: isRowsLoading } = useGetMeiaStudyP4E2eRows({
    enabled: canLoadP4Data,
    studyId,
  });
  const { data: threatTargets = [], isLoading: isTargetsLoading } = useGetMeiaStudyThreatTargets({
    enabled: canLoadP4Data,
    studyId,
  });
  const { data: runCases = [] } = useGetMeiaRunCases({ studyId });
  const { data: scoringOutputs = [] } = useGetMeiaScoringOutputs({ enabled: runCases.length > 0, studyId });
  const latestRunCaseId = useMemo(
    () => getDefaultBriefRunCaseId({ runCases, scoringOutputs }),
    [runCases, scoringOutputs],
  );
  const latestRunCase = runCases.find((runCase) => runCase.id === latestRunCaseId);
  const { data: latestDashboard } = useGetMeiaRunCaseDashboard({
    enabled: Boolean(latestRunCaseId),
    runCaseId: latestRunCaseId ?? undefined,
    studyId,
  });
  const studyVerdict = useMemo(
    () => (latestDashboard ? getBriefStudyVerdict(latestDashboard.missions) : null),
    [latestDashboard],
  );
  const g4Gate = useMemo(
    () => latestDashboard?.gateCards.find((gate) => gate.gate === StudyGate.G4) ?? null,
    [latestDashboard],
  );

  const handleSelectSubtab = (value: string): void => {
    if (!isMeiaP4SubTab(value)) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('subtab', value);
        return next;
      },
      { preventScrollReset: true, replace: false },
    );
  };

  const tasksCount = countUniqueTasks(rows);
  const technologiesCount = countUniqueTechnologies(rows);
  const threadsCount = new Set(rows.map((row) => row.threadId)).size;
  const firstMissionId = missions[0]?.id;
  const firstThread = threads[0];

  const handleAddMissionInP1 = (): void => {
    navigate(meiaRoute.studyTab({ query: { action: 'addMission' }, studyId, tab: 'p1' }));
  };

  const handleAddThreadsInP2 = (): void => {
    if (!firstMissionId) return;
    navigate(
      meiaRoute.studyTab({
        query: { action: 'addThread', missionId: firstMissionId },
        studyId,
        tab: 'p2',
      }),
    );
  };

  const handleAddTasksInP3 = (): void => {
    if (!firstThread) return;
    navigate(
      meiaRoute.studyTab({
        query: { missionId: firstThread.missionId, threadId: firstThread.id },
        studyId,
        tab: 'p3',
      }),
    );
  };

  const emptyState = isPrerequisiteLoading
    ? null
    : hasMissions
      ? hasThreads
        ? hasTasks
          ? null
          : {
              ...(canEdit ? { message: 'Add Tasks in P3', onClick: handleAddTasksInP3 } : {}),
              title: 'No Tasks',
            }
        : {
            ...(canEdit ? { message: 'Add Threads in P2', onClick: handleAddThreadsInP2 } : {}),
            title: 'No Mission Threads',
          }
      : {
          ...(canEdit ? { message: 'Add Mission in P1', onClick: handleAddMissionInP1 } : {}),
          title: 'No Missions',
        };

  if (emptyState) {
    return (
      <EmptyStateContainer
        className="min-h-[520px] rounded-xl bg-white shadow-sm"
        message={emptyState.message}
        onClick={emptyState.onClick}
        title={emptyState.title}
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div>
        <BlueLabel
          className="text-xs"
          text="Phase 4 — Scoring & Verdicts"
        />
        <h2 className="mt-1 text-lg font-semibold text-gray-950">{subTabTitles[activeSubtab]}</h2>
        <p className="mt-1 text-xs text-gray-500">
          {latestRunCase && <>Epoch: {formatEpochLabel(latestRunCase.epoch)} · </>}
          Aggregation:{' '}
          {SCORING_CONFIG_AGGREGATION_OPTIONS.find((option) => option.value === study.scoringConfig.aggregationMethod)
            ?.label ?? study.scoringConfig.aggregationMethod}
        </p>
      </div>

      <SimpleTabs
        activeTab={activeSubtab}
        fullWidth={false}
        onTabChange={handleSelectSubtab}
        tabs={subTabs}
        variant="underline"
      />

      {isPrerequisiteLoading ? (
        <Skeleton className="min-h-[520px] w-full rounded-xl bg-white shadow-sm" />
      ) : (
        <>
          <MeiaP4KpiStrip
            g4Gate={g4Gate}
            isLoading={isRowsLoading || isPrerequisiteLoading}
            studyVerdict={studyVerdict}
            tasksCount={tasksCount}
            technologiesCount={technologiesCount}
            threadsCount={threadsCount}
          />

          {activeSubtab === 'e2e' && (
            <MeiaP4E2eView
              isLoadingRows={isRowsLoading || isPrerequisiteLoading}
              isLoadingTargets={isTargetsLoading || isPrerequisiteLoading}
              onSelectThread={setSelectedThreadId}
              rows={rows}
              selectedThreadId={selectedThreadId}
              threatTargets={threatTargets}
            />
          )}

          {activeSubtab === 'runcases' && (
            <MeiaP4RunCasesView
              canEdit={canEdit}
              studyId={studyId}
            />
          )}

          {activeSubtab === 'matrix' && (
            <MeiaP4RunCasesView
              canEdit={canEdit}
              studyId={studyId}
              view="matrix"
            />
          )}

          <MeiaP4AdvanceBar
            studyId={studyId}
            studyVerdict={studyVerdict}
          />
        </>
      )}
    </div>
  );
};
